import { z } from "zod";
import {
  StepupFrequency,
  TransactionOrderType,
  TransactionType,
} from "../../constants";

export const planFrequencySchema = z.enum(["DAILY", "WEEKLY", "MONTHLY"]);

export const stepupFrequencySchema = z.nativeEnum(StepupFrequency).nullable();

// Enums
export const transactionTypeSchema = z.nativeEnum(TransactionType);

export const transactionOrderTypeSchema = z.nativeEnum(TransactionOrderType);

export const transactionOrderSubmissionStatusSchema = z.enum([
  "DRAFT",
  "SUBMITTED",
  "FAILED",
]);

// Base Schemas
const baseTransactionOrderPayloadSchema = z.object({
  investment_account_guid: z.string(),
});

const baseOneTimeTransactionOrderSchema = z.object({
  scheme_isin: z.string(),
  folio_id: z.number().or(z.string()).nullable(),
  amount: z.string(),
});

const baseSipTransactionOrderSchema = z.object({
  scheme_isin: z.string(),
  folio_id: z.number().or(z.string()).nullable(),
  amount: z.string(),
  frequency: planFrequencySchema,
  start_date: z.string(),
  first_order_today: z.boolean(),
  mandate_id: z.number(),
  step_up_frequency: stepupFrequencySchema,
  step_up_amount: z.number().nullable(),
  step_up_percentage: z.number().nullable(),
  source_basket_id: z.number().nullable().optional(),
  old_plan_details: z
    .object({
      mandate_id: z.number(),
      amount: z.string(),
      start_date: z.string(),
      id: z.number(),
      step_up_frequency: stepupFrequencySchema,
      step_up_amount: z.number().nullable(),
      step_up_percentage: z.number().nullable(),
    })
    .optional(),
});

const baseSwpTransactionOrderPayloadSchema = z.object({
  scheme_isin: z.string(),
  folio_id: z.number(),
  number_of_withdrawals: z.string(),
  frequency: planFrequencySchema,
  amount: z.string().optional().nullable(),
  units: z.string().optional().nullable(),
  start_date: z.string(),
  first_order_today: z.boolean(),
});

const baseStpTransactionOrderSchema = z.object({
  switch_out_scheme_isin: z.string(),
  folio_id: z.number(),
  switch_in_scheme_isin: z.string(),
  number_of_transfers: z.string(),
  frequency: planFrequencySchema,
  amount: z.string().optional().nullable(),
  units: z.string().optional().nullable(),
  start_date: z.string(),
  first_order_today: z.boolean(),
});

const baseRedemptionTransactionOrderSchema = z.object({
  scheme_isin: z.string(),
  folio_id: z.number(),
  amount: z.string().optional().nullable(),
  units: z.string().optional().nullable(),
});

const baseSwitchTransactionOrderSchema = z.object({
  folio_id: z.number(),
  amount: z.string().optional().nullable(),
  units: z.string().optional().nullable(),
});

// Combined Payload Schemas
export const oneTimeTransactionOrderPayloadSchema =
  baseTransactionOrderPayloadSchema.merge(baseOneTimeTransactionOrderSchema);

export const sipTransactionOrderPayloadSchema =
  baseTransactionOrderPayloadSchema.merge(baseSipTransactionOrderSchema);

export const swpTransactionOrderPayloadSchema =
  baseTransactionOrderPayloadSchema.merge(baseSwpTransactionOrderPayloadSchema);

export const stpTransactionOrderPayloadSchema =
  baseTransactionOrderPayloadSchema.merge(baseStpTransactionOrderSchema);

export const redemptionTransactionOrderPayloadSchema =
  baseTransactionOrderPayloadSchema.merge(baseRedemptionTransactionOrderSchema);

export const switchTransactionOrderPayloadSchema =
  baseTransactionOrderPayloadSchema.merge(baseSwitchTransactionOrderSchema);

// Base Transaction Order Schema
export const baseTransactionOrderSchema = z.object({
  id: z.number(),
  investment_account: z.string(),
  units: z.string(),
  ref_id: z.string(),
  submission_status: transactionOrderSubmissionStatusSchema,
  user_remarks: z.string(),
  nse_reg_status: z.string(),
  nse_reg_remark: z.string(),
  folioDetails: z
    .object({
      folio_number: z.string(),
    })
    .optional(),
  type: transactionOrderTypeSchema,
  amount: z.string(),
  amc_morningstar_code: z.string(),
  action_type: z.enum(["CANCEL", "CREATE", "PAUSE", "RESUME", "MODIFY"]),
});

const baseNonSwitchTypeTransactionOrderSchema = z.object({
  scheme_name: z.string(),
  scheme_id: z.number(),
});

const baseSwitchTypeTransactionOrderSchema = z.object({
  switch_out_scheme_name: z.string(),
  switch_in_scheme_name: z.string(),
  switch_out_scheme_id: z.number(),
  switch_in_scheme_id: z.number(),
  switch_out_scheme_isin: z.string(),
  switch_in_scheme_isin: z.string(),
});

// Final Transaction Order Schemas
export const purchaseTransactionOrderSchema = baseTransactionOrderSchema
  .merge(baseNonSwitchTypeTransactionOrderSchema)
  .merge(baseOneTimeTransactionOrderSchema)
  .merge(
    z.object({
      type: z.literal(transactionOrderTypeSchema.enum.PURCHASE),
      folio_id: z.number(),
    })
  );

export const xsipTransactionOrderSchema = baseTransactionOrderSchema
  .merge(baseNonSwitchTypeTransactionOrderSchema)
  .merge(baseSipTransactionOrderSchema)
  .merge(
    z.object({
      type: z.literal(transactionOrderTypeSchema.enum.XSIP),
      folio_id: z.number(),
    })
  );

export const redemptionTransactionOrderSchema = baseTransactionOrderSchema
  .merge(baseNonSwitchTypeTransactionOrderSchema)
  .merge(baseRedemptionTransactionOrderSchema)
  .merge(
    z.object({
      type: z.literal(transactionOrderTypeSchema.enum.REDEMPTION),
    })
  );

export const swpTransactionOrderSchema = baseTransactionOrderSchema
  .merge(baseNonSwitchTypeTransactionOrderSchema)
  .merge(baseSwpTransactionOrderPayloadSchema)
  .merge(
    z.object({
      type: z.literal(transactionOrderTypeSchema.enum.SWP),
    })
  );

export const switchTransactionOrderSchema = baseTransactionOrderSchema
  .merge(baseSwitchTypeTransactionOrderSchema)
  .merge(baseSwitchTransactionOrderSchema)
  .merge(
    z.object({
      type: z.literal(transactionOrderTypeSchema.enum.SWITCH),
    })
  );

export const stpTransactionOrderSchema = baseTransactionOrderSchema
  .merge(baseSwitchTypeTransactionOrderSchema)
  .merge(baseStpTransactionOrderSchema)
  .merge(
    z.object({
      type: z.literal(transactionOrderTypeSchema.enum.STP),
    })
  );

// Union type for all transaction orders
export const transactionOrderSchema = z.discriminatedUnion("type", [
  purchaseTransactionOrderSchema,
  xsipTransactionOrderSchema,
  redemptionTransactionOrderSchema,
  swpTransactionOrderSchema,
  switchTransactionOrderSchema,
  stpTransactionOrderSchema,
]);

export const folioNumberSchema = z.object({
  id: z.number(),
  created_at: z.string(),
  modified_at: z.string(),
  folio_number: z.string(),
  mf_investment_account: z.string(),
  amc_id: z.number(),
  internal_flag: z.boolean(),
});
